import { Story } from "@/components/builder/stories/context";
import TranslatedLink from "@/libs/base/components/translated-link";
import { cn } from "@/libs/base/utils/layout";
import { BuilderReference } from "@/libs/builder/models/data-types";
import { BuilderIoQueryBuilder } from "@/libs/builder/services/builder-io-query-builder";
import { Builder } from "@builder.io/react";
import { useEffect, useState } from "react";
import { useStoriesListContext } from "./context";
import { buildStoryUrl } from "./helper";

interface ComponentProps {
  story: BuilderReference<Story>;
}

export default function StoryHighlight(props: ComponentProps) {
  const [story, setStory] = useState<Story | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const storiesContext = useStoriesListContext();

  useEffect(() => {
    if (!isLoading || !props.story?.id) {
      return;
    }

    let storyPromise: Promise<Story | null>;
    if (props.story.value) {
      storyPromise = Promise.resolve(props.story.value);
    } else {
      storyPromise = storiesContext.service.getStory({
        query: new BuilderIoQueryBuilder().addEquals("id", props.story.id).build(),
        omit: "data.blocks",
      });
    }

    storyPromise
      .then((story) => setStory(story))
      .catch((e) => console.log("failed to fetch story", props.story.id, e))
      .finally(() => setIsLoading(false));
  }, [isLoading]);

  if (!story) {
    return <div>No story selected</div>;
  }

  if ((storiesContext.loadingStories && !Builder.isEditing) || storiesContext.filter.genres.length > 0) {
    // do not render the highlight if genre filter is active or stories are loading
    return null;
  }

  return (
    <div className="group flex w-full flex-row flex-wrap">
      <div className="aspect-2 w-full overflow-hidden sm:basis-1/2">
        <TranslatedLink href={buildStoryUrl(story)}>
          {story.data.previewImage && (
            <img
              src={story.data.previewImage}
              alt="Image"
              className={cn(
                "m-0 h-full w-full object-cover p-0",
                "grayscale-0 group-hover:scale-125 group-hover:grayscale",
                "transform transition-all duration-1000",
              )}
            />
          )}
        </TranslatedLink>
      </div>
      <div
        className={cn(
          "aspect-2 w-full sm:basis-1/2",
          "flex flex-col items-start justify-center",
          "px-12",
          "bg-black text-white",
        )}
      >
        {story.data.genres && story.data.genres.length > 0 && (
          <div className="mb-3.5 flex flex-row gap-2">
            {story.data.genres.map((genre) => (
              <div
                key={genre}
                onClick={() => storiesContext.selectGenres([genre])}
                className={cn(
                  "cursor-pointer",
                  "bg-white text-xs font-medium uppercase text-black",
                  "rounded-full px-2.5 py-0.5",
                  "transition duration-300 hover:bg-gray-200",
                )}
              >
                {genre}
              </div>
            ))}
          </div>
        )}
        <TranslatedLink
          href={buildStoryUrl(story)}
          className="line-clamp-4 transform text-4xl transition-transform duration-1000 group-hover:scale-105"
        >
          {story.data.title}
        </TranslatedLink>
      </div>
    </div>
  );
}
